import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Paper from '@material-ui/core/Paper';
import {
  Chart,
  BarSeries,
  LineSeries,
  Title,
  ArgumentAxis,
  ValueAxis,
  Legend,
  Tooltip,
} from '@devexpress/dx-react-chart-material-ui';
import { Animation, EventTracker, Stack } from '@devexpress/dx-react-chart';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { MonthlyTrendsSkeleton } from './SkeletonComponents';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  summary: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  summaryItem: {
    textAlign: 'center',
  },
  summaryValue: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

const MonthlyTrendsChart = ({ data }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (!data) {
    return <MonthlyTrendsSkeleton />;
  }

  if (data.length === 0) {
    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" align="center">
          {t('dashboard.monthly_trends')}
        </Typography>
        <Typography variant="body2" align="center" color="textSecondary">
          {t('dashboard.no_data')}
        </Typography>
      </Paper>
    );
  }

  const chartData = data.map((item) => ({
    month: item.monthName,
    incidences: item.incidences,
    assistance: item.assistance,
    orders: item.orders,
    total: item.total,
  }));

  const totalIncidences = data.reduce((sum, item) => sum + item.incidences, 0);
  const totalAssistance = data.reduce((sum, item) => sum + item.assistance, 0);
  const totalOrders = data.reduce((sum, item) => sum + item.orders, 0);
  const totalRevenue = data.reduce((sum, item) => sum + item.total, 0);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Paper className={classes.paper}>
      <Chart data={chartData}>
        <ArgumentAxis />
        <ValueAxis />
        
        <BarSeries
          name={t('charts.monthly.legend.incidences')}
          valueField="incidences"
          argumentField="month"
          color="#ff7043"
        />
        <BarSeries
          name={t('charts.monthly.legend.assistance')}
          valueField="assistance"
          argumentField="month"
          color="#9ccc65"
        />
        <BarSeries
          name={t('charts.monthly.legend.orders')}
          valueField="orders"
          argumentField="month"
          color="#42a5f5"
        />
        
        <Stack />
        <Animation />
        <Legend position="bottom" />
        <Title text={t('dashboard.monthly_trends')} />
        <EventTracker />
        <Tooltip />
      </Chart>

      <Box className={classes.summary}>
        <Box className={classes.summaryItem}>
          <Typography className={classes.summaryValue} style={{ color: '#ff7043' }}>
            {totalIncidences}
          </Typography>
          <Typography className={classes.summaryLabel}>
            {t('charts.monthly.legend.incidences')}
          </Typography>
        </Box>
        <Box className={classes.summaryItem}>
          <Typography className={classes.summaryValue} style={{ color: '#9ccc65' }}>
            {totalAssistance}
          </Typography>
          <Typography className={classes.summaryLabel}>
            {t('charts.monthly.legend.assistance')}
          </Typography>
        </Box>
        <Box className={classes.summaryItem}>
          <Typography className={classes.summaryValue} style={{ color: '#42a5f5' }}>
            {totalOrders}
          </Typography>
          <Typography className={classes.summaryLabel}>
            {t('charts.monthly.legend.orders')}
          </Typography>
        </Box>
        <Box className={classes.summaryItem}>
          <Typography className={classes.summaryValue} style={{ color: '#4caf50' }}>
            {formatCurrency(totalRevenue)}
          </Typography>
          <Typography className={classes.summaryLabel}>
            {t('dashboard.estimated_revenue')}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default MonthlyTrendsChart;
